import { useMsal } from "@azure/msal-react";
import { addLocale } from "primereact/api";
import { ChangeEvent, useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";

import { axiosGetRequest } from "../api/ApiClient";
import InputSelect from "../components/InputSelect";
import InputText from "../components/InputText";
import { ptBrDatepickerTranslations } from "../i18n/locales/pt-br";
import CargasHistoricoFilterDTO from "../model/dto/CargasHistoricoFilterDTO";
import OptionsSelect from "../model/OptionsSelect";
import ButtonStyles from "../styles/components/ButtonStyles";
import InputCalendarStyles from "../styles/components/InputCalendarStyles";
import TableStyles from "../styles/components/TableStyles";
import {
    StyledHeader,
    StyledSubTitle,
    StyledTitle,
} from "../styles/MainStyles";
import { MASK_DATE, MASK_DATE_BR, NO_RECORD_FOUND } from "../utils/constants";
import GenericUtils from "../utils/GenericUtils";
import StringUtils from "../utils/types/StringUtils";

const { BtnPrimary, BtnOutlinePrimary, BtnTable } = ButtonStyles;
const { TablePrimary, TitleTable } = TableStyles;

addLocale("pt-BR", ptBrDatepickerTranslations);

function ImportHistory() {
    const { instance } = useMsal();
    const acessToken = GenericUtils.getAccessTokenTemplateManager();

    const pageItem = "page-item";
    const active = "active";

    // Default states
    const [history, setHistory] = useState<any[]>([]);
    const [status, setStatus] = useState<any[]>([]);
    const [selectStatus, setSelectStatus] = useState("");
    const [templates, setTemplates] = useState<any[]>([]);
    const [selectTemplate, setSelectTemplate] = useState("");
    const [datePicker, setDatePicker] = useState<any>(null);
    const [inputName, setInputName] = useState("");

    // Pagination states
    const [itensPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [firstPage, setFirstPage] = useState(0);
    const [secondPage, setSecondPage] = useState(0);
    const [thirdPage, setThirdPage] = useState(0);
    const [lastPage, setLastPage] = useState(0);
    const [seachParams, setSeachParams] = useState<CargasHistoricoFilterDTO>();

    const getStatusName = (statusId: number) => {
        switch (statusId) {
            case 1:
                return "Em espera";
            case 2:
                return "Em processamento";
            case 3:
                return "Concluído";
            case 4:
                return "Arquivo com erro";
            case 5:
                return "Arquivo com erro";
            default:
                return "-";
        }
    };

    const getStatusClass = (statusId: number): string => {
        switch (statusId) {
            case 1:
                return "yellow-color";
            case 2:
                return "yellow-color";
            case 3:
                return "green-color";
            case 4:
                return "red-color";
            case 5:
                return "red-color";
            default:
                return "red-color";
        }
    };

    function clearPagination(response?: any) {
        setSeachParams(undefined);
        setTotalRecords(response?.data?.length || 0);
        setCurrentPage(0);
        setFirstPage(0);
        setSecondPage(0);
        setThirdPage(0);
        setLastPage(0);
    }

    function updatePagination(response: any, offset: number) {
        const tempCurrentPage = offset;
        const tempTotalRecords = response.headers["content-range"].split("/")[1]
            ? Number(response.headers["content-range"].split("/")[1])
            : 0;
        const tempLastPage = Math.ceil(tempTotalRecords / itensPerPage) - 1;
        setCurrentPage(tempCurrentPage);
        setTotalRecords(tempTotalRecords);
        setLastPage(tempLastPage);
        if (tempLastPage - tempCurrentPage === tempLastPage) {
            setFirstPage(tempCurrentPage);
            setSecondPage(tempCurrentPage + 1);
            setThirdPage(tempCurrentPage + 2);
        } else if (
            tempLastPage - tempCurrentPage < tempLastPage &&
            tempLastPage - tempCurrentPage > 0
        ) {
            setFirstPage(tempCurrentPage - 1);
            setSecondPage(tempCurrentPage);
            setThirdPage(tempCurrentPage + 1);
        } else if (tempLastPage - tempCurrentPage === 0) {
            setFirstPage(tempCurrentPage - 2);
            setSecondPage(tempCurrentPage - 1);
            setThirdPage(tempCurrentPage);
        }
    }

    async function getLoadStatus() {
        const responseList: OptionsSelect[] = [
            {
                value: "",
                text: "Status",
            },
        ];
        const response = await axiosGetRequest(
            "/template-manager/v1/cargas/carga-status",
            acessToken,
            instance
        );
        if (response.status === 200 && response?.data) {
            response?.data
                ?.filter((value: any) => value.cargaStatusId !== 5) // Removendo o status de erro do sistema
                .map((item: any) =>
                    responseList.push({
                        value: item.cargaStatusId,
                        text: item.cargaStatusDescricao,
                    })
                );
        }
        setStatus(responseList);
    }

    async function getTemplateFileType() {
        const responseList: OptionsSelect[] = [
            {
                value: "",
                text: "Tipo de importação",
            },
        ];
        const response = await axiosGetRequest(
            "/template-manager/v1/cargas/tipo-arquivo-template",
            acessToken,
            instance
        );
        if (response.status === 200 && response?.data) {
            response?.data?.map((item: any) =>
                responseList.push({
                    value: item.tipoArquivoTemplateId,
                    text:
                        item.tipoArquivoTemplateId === 10
                            ? "Hora Letiva RE"
                            : item.descricao,
                })
            );
        }
        setTemplates(responseList);
    }

    async function getHistory(dto?: CargasHistoricoFilterDTO) {
        const response = await axiosGetRequest(
            `/template-manager/v1/templates/cargas-historico${StringUtils.getQueryParams(
                dto
            )}`,
            acessToken,
            instance
        );
        if (response.status === 206 && response?.data?._result) {
            updatePagination(response, Number(dto?._offset) || 0);
            setHistory(response.data._result);
        } else if (response.status === 200 && response.data) {
            setHistory(response.data);
            clearPagination(response);
        } else {
            setHistory([]);
            clearPagination();
        }
    }

    function downloadFile(fileName: string) {
        axiosGetRequest(
            `/template-manager/v1/templates/downloads/${fileName}`,
            acessToken,
            instance,
            true
        ).then((response) => {
            if (response?.data?.url) {
                window.open(response.data.url, "_blank");
            }
        });
    }

    function handleClearFilter() {
        setSelectTemplate("");
        setSelectStatus("");
        setDatePicker(null);
        setInputName("");
    }

    const handleSelectTemplateChange = (
        event: ChangeEvent<{ value: unknown }>
    ) => {
        setSelectTemplate(event.target.value as string);
    };

    const handleSelectStatusChange = (
        event: ChangeEvent<{ value: unknown }>
    ) => {
        setSelectStatus(event.target.value as string);
    };

    const handleInputNameChange = (event: ChangeEvent<{ value: unknown }>) => {
        setInputName(event.target.value as string);
    };

    function handleSearch() {
        const dto: CargasHistoricoFilterDTO = {
            _limit: String(itensPerPage),
            asc: "false",
            desc: "true",
            _offset: "0",
            tipo_arquivo_template: selectTemplate,
            status: selectStatus,
            carga_data: StringUtils.formatDateString(
                datePicker?.toISOString(),
                MASK_DATE
            ),
            usuario: inputName,
            nome_arquivo: "",
            carga_hora: "",
        };
        setSeachParams(dto);
        getHistory(dto);
    }

    function handlePaginate(page: string) {
        const dto: CargasHistoricoFilterDTO = {
            _limit: String(itensPerPage),
            asc: "false",
            desc: "true",
            _offset: page || String(currentPage),
            tipo_arquivo_template: seachParams?.tipo_arquivo_template || "",
            status: seachParams?.status || "",
            carga_data: seachParams?.carga_data || "",
            usuario: seachParams?.usuario || "",
            nome_arquivo: "",
            carga_hora: "",
        };
        setSeachParams(dto);
        getHistory(dto);
    }

    function handleDownload(nomeArquivo: string) {
        if (nomeArquivo) {
            downloadFile(nomeArquivo);
        }
    }

    useEffect(() => {
        getLoadStatus();
        getTemplateFileType();
        handleSearch();
    }, []);

    const firstPageView = firstPage >= 0 && (
        <li
            className={
                currentPage === firstPage
                    ? `${pageItem} ${active}`
                    : `${pageItem}`
            }
        >
            <Button
                className="page-link"
                href="#"
                onClick={() => handlePaginate(String(firstPage))}
            >
                {firstPage + 1}
            </Button>
        </li>
    );

    const secondPageView = (
        <li
            className={
                currentPage === secondPage
                    ? `${pageItem} ${active}`
                    : `${pageItem}`
            }
        >
            <Button
                className="page-link"
                href="#"
                onClick={() => handlePaginate(String(secondPage))}
            >
                {secondPage + 1}
            </Button>
        </li>
    );

    const thirdPageView = thirdPage <= lastPage && (
        <li
            className={
                currentPage === thirdPage
                    ? `${pageItem} ${active}`
                    : `${pageItem}`
            }
        >
            <Button
                className="page-link"
                href="#"
                onClick={() => handlePaginate(String(thirdPage))}
            >
                {thirdPage + 1}
            </Button>
        </li>
    );

    return (
        <>
            <StyledHeader>
                <StyledTitle>Arquivos Importados</StyledTitle>
                <StyledSubTitle>
                    Utilize os filtros abaixo para facilitar sua busca
                </StyledSubTitle>
            </StyledHeader>
            <Row>
                <Col sm="12" md="4" lg="3">
                    <InputSelect
                        value={selectTemplate}
                        handleSelectChange={handleSelectTemplateChange}
                        optionsSelect={templates}
                    />
                </Col>
                <Col sm="12" md="4" lg="2">
                    <InputSelect
                        value={selectStatus}
                        handleSelectChange={handleSelectStatusChange}
                        optionsSelect={status}
                    />
                </Col>
                <Col sm="12" md="4" lg="2" className="mb-4">
                    <InputCalendarStyles.StyledCalendar
                        placeholder="Data"
                        dateFormat="dd/mm/yy"
                        locale="pt-BR"
                        showButtonBar
                        readOnlyInput
                        value={datePicker}
                        onChange={(e: any) => setDatePicker(e.target?.value)}
                    />
                </Col>
                <Col sm="12" md="4" lg="2" className="mb-4">
                    <InputText
                        placeholder="Usuário"
                        value={inputName}
                        onChange={handleInputNameChange}
                    />
                </Col>
                <Col xs="6" md="auto" lg="auto">
                    <BtnPrimary onClick={() => handleSearch()}>
                        Filtrar
                    </BtnPrimary>
                </Col>
                <Col xs="6" md="auto" lg="auto">
                    <BtnOutlinePrimary
                        onClick={() => {
                            handleClearFilter();
                            handleSearch();
                        }}
                    >
                        Limpar
                    </BtnOutlinePrimary>
                </Col>
            </Row>
            <Row className="mt-3">
                <Col>
                    <TitleTable>{totalRecords} arquivos encontrados</TitleTable>
                </Col>
            </Row>
            <Row>
                <Col>
                    <TablePrimary bordered striped responsive>
                        <thead>
                            <tr>
                                <th>Nome do arquivo</th>
                                <th>Total de linhas</th>
                                <th>Linhas com erro</th>
                                <th>Data</th>
                                <th>Usuário</th>
                                <th>Observação</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history?.length === 0 ? (
                                <tr className="table-empty">
                                    <td colSpan={7}>{NO_RECORD_FOUND}</td>
                                </tr>
                            ) : (
                                history.map((item) => (
                                    <tr key={item.cargaHistoricoId}>
                                        <td>{item.nomeArquivo}</td>
                                        <td>{item.qtTotalLinhasArquivo}</td>
                                        <td>
                                            {item.qtLinhasArquivoComErro.trim() ===
                                            "0"
                                                ? "-"
                                                : item.qtLinhasArquivoComErro}
                                        </td>
                                        <td>
                                            {StringUtils.formatDateString(
                                                item.cargaData,
                                                MASK_DATE_BR
                                            )}
                                            {" - "}
                                            {StringUtils.formatDateString(
                                                `${
                                                    item.cargaData
                                                } ${item.cargaHora.substring(
                                                    0,
                                                    8
                                                )}`,
                                                "HH'h'mm"
                                            )}
                                        </td>
                                        <td>{item.usuario}</td>
                                        <td>
                                            {StringUtils.capitalizeFirstAndToLower(
                                                item.observacao
                                            )}
                                        </td>
                                        <td>
                                            <BtnTable
                                                variant="link"
                                                className={getStatusClass(
                                                    item?.cargaStatus
                                                        ?.cargaStatusId
                                                )}
                                                disabled={
                                                    item?.cargaStatus
                                                        ?.cargaStatusId !== 4 &&
                                                    item?.cargaStatus
                                                        ?.cargaStatusId !== 5
                                                }
                                                onClick={() =>
                                                    handleDownload(
                                                        item.nomeArquivo
                                                    )
                                                }
                                            >
                                                {getStatusName(
                                                    item?.cargaStatus
                                                        ?.cargaStatusId
                                                )}
                                            </BtnTable>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </TablePrimary>
                </Col>
            </Row>
            {totalRecords > itensPerPage && (
                <nav aria-label="Page navigation example">
                    <ul className="pagination justify-content-end">
                        {currentPage > firstPage ? (
                            <li className="page-item">
                                <Button
                                    className="page-link"
                                    href="#"
                                    onClick={() =>
                                        handlePaginate(String(currentPage - 1))
                                    }
                                >
                                    Anterior
                                </Button>
                            </li>
                        ) : null}
                        {firstPageView}
                        {secondPageView}
                        {thirdPageView}
                        {currentPage < lastPage && (
                            <li className="page-item">
                                <Button
                                    className="page-link"
                                    href="#"
                                    onClick={() =>
                                        handlePaginate(String(currentPage + 1))
                                    }
                                >
                                    Próximo
                                </Button>
                            </li>
                        )}
                    </ul>
                </nav>
            )}
        </>
    );
}

export default ImportHistory;
